import { create } from 'zustand';
import api from '@/lib/api';

export type Tone = 'professional' | 'casual' | 'witty' | 'inspirational' | 'technical';
export type PersonaLanguage = 'it' | 'en' | 'pl';

export interface Persona {
  id: string;
  user_id: string;
  name: string;
  tone: string;
  archetype?: string | null;
  style_notes: string | null;
  target_audience: string | null;
  topics: string[];
  language: PersonaLanguage | string;
  is_default: boolean;
  created_at: string;
  updated_at: string;
  targets_count?: number;
  network_count?: number;
  posts_generated?: number;
  voice_summary?: string | null;
  offer?: string | null;
  positioning?: string | null;
  content_pillars?: string[];
}

export interface CreatePersonaInput {
  name: string;
  tone: Tone;
  archetype?: string;
  style_notes?: string;
  target_audience?: string;
  topics: string[];
  language: PersonaLanguage;
  voice_summary?: string;
  offer?: string;
  positioning?: string;
  content_pillars?: string[];
}

export interface UpdatePersonaInput {
  name?: string;
  tone?: Tone;
  archetype?: string;
  style_notes?: string;
  target_audience?: string;
  topics?: string[];
  language?: PersonaLanguage;
  voice_summary?: string;
  offer?: string;
  positioning?: string;
  content_pillars?: string[];
}

interface PersonasState {
  personas: Persona[];
  selectedPersona: Persona | null;
  isLoading: boolean;
  error: string | null;
  fetchPersonas: () => Promise<void>;
  createPersona: (data: CreatePersonaInput) => Promise<void>;
  updatePersona: (id: string, data: UpdatePersonaInput) => Promise<void>;
  deletePersona: (id: string) => Promise<void>;
  setSelectedPersona: (persona: Persona | null) => void;
  setDefaultPersona: (id: string) => Promise<void>;
  getStats: () => { total: number; defaultCount: number; generatedCount: number };
}

function parseApiError(error: unknown, fallback: string): string {
  if (
    typeof error === 'object' &&
    error !== null &&
    'response' in error &&
    typeof (error as { response?: unknown }).response === 'object'
  ) {
    const response = (error as {
      response?: { data?: { error?: string; message?: string; detail?: string } };
    }).response;
    return response?.data?.error ?? response?.data?.message ?? response?.data?.detail ?? fallback;
  }
  return fallback;
}

export const usePersonasStore = create<PersonasState>()((set, get) => ({
  personas: [],
  selectedPersona: null,
  isLoading: false,
  error: null,

  fetchPersonas: async () => {
    set({ isLoading: true, error: null });
    try {
      const response = await api.get<{ data: Persona[] }>('/personas');
      const personas = response.data.data ?? [];

      const currentSelectedId = get().selectedPersona?.id;
      const selectedPersona =
        personas.find((persona) => persona.id === currentSelectedId) ??
        personas.find((persona) => persona.is_default) ??
        personas[0] ??
        null;

      set({ personas, selectedPersona, isLoading: false });
    } catch (error: unknown) {
      set({ error: parseApiError(error, 'Failed to fetch personas'), isLoading: false });
    }
  },

  createPersona: async (data) => {
    set({ isLoading: true, error: null });
    try {
      const response = await api.post<{ data: Persona }>('/personas', data);
      const createdPersona = response.data.data;

      set((state) => ({
        personas: [...state.personas, createdPersona],
        selectedPersona: state.selectedPersona ?? createdPersona,
        isLoading: false,
      }));
    } catch (error: unknown) {
      const parsedError = parseApiError(error, 'Failed to create persona');
      set({ error: parsedError, isLoading: false });
      throw new Error(parsedError);
    }
  },

  updatePersona: async (id, data) => {
    set({ isLoading: true, error: null });
    try {
      const response = await api.put<{ data: Persona }>(`/personas/${id}`, data);
      const updatedPersona = response.data.data;

      set((state) => ({
        personas: state.personas.map((persona) =>
          persona.id === id ? { ...persona, ...updatedPersona } : persona,
        ),
        selectedPersona:
          state.selectedPersona?.id === id
            ? { ...state.selectedPersona, ...updatedPersona }
            : state.selectedPersona,
        isLoading: false,
      }));
    } catch (error: unknown) {
      const parsedError = parseApiError(error, 'Failed to update persona');
      set({ error: parsedError, isLoading: false });
      throw new Error(parsedError);
    }
  },

  deletePersona: async (id) => {
    set({ isLoading: true, error: null });
    try {
      await api.delete(`/personas/${id}`);

      set((state) => {
        const personas = state.personas.filter((persona) => persona.id !== id);
        const selectedPersona =
          state.selectedPersona?.id === id
            ? personas.find((persona) => persona.is_default) ?? personas[0] ?? null
            : state.selectedPersona;

        return { personas, selectedPersona, isLoading: false };
      });
    } catch (error: unknown) {
      const parsedError = parseApiError(error, 'Failed to delete persona');
      set({ error: parsedError, isLoading: false });
      throw new Error(parsedError);
    }
  },

  setSelectedPersona: (persona) => {
    set({ selectedPersona: persona });
  },

  setDefaultPersona: async (id) => {
    set({ isLoading: true, error: null });
    try {
      await api.put(`/personas/${id}/default`);

      set((state) => {
        const updatedPersonas = state.personas.map((persona) => ({
          ...persona,
          is_default: persona.id === id,
        }));
        const updatedSelectedPersona = updatedPersonas.find((persona) => persona.id === id) ?? null;

        return {
          personas: updatedPersonas,
          selectedPersona: updatedSelectedPersona,
          isLoading: false,
        };
      });
    } catch (error: unknown) {
      const parsedError = parseApiError(error, 'Failed to set default persona');
      set({ error: parsedError, isLoading: false });
      throw new Error(parsedError);
    }
  },

  getStats: () => {
    const personas = get().personas;
    return {
      total: personas.length,
      defaultCount: personas.filter((persona) => persona.is_default).length,
      generatedCount: personas.reduce((acc, persona) => acc + (persona.posts_generated ?? 0), 0),
    };
  },
}));
